/**
 * Literafan Image Preloader Utility
 * Pre-heats and caches in browser memory key background images and article covers
 * using the local image proxy to make page navigation and section rendering instantaneous.
 */
import { getOptimizedImageUrl } from './imageOptimizer';

// Only lightweight, optimized assets needed for immediate navigation, respecting data limits
const CORE_BACKGROUND_IMAGES = [
  'https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png' // Logo
];

const preloadedSet = new Set<string>();

/**
 * Preload a single image URL in memory
 */
export function preloadImage(src: string, priority: 'high' | 'auto' | 'low' = 'low'): Promise<boolean> {
  if (!src || typeof window === 'undefined') return Promise.resolve(false);
  const optimizedUrl = getOptimizedImageUrl(src);
  if (preloadedSet.has(optimizedUrl)) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.onload = () => {
      preloadedSet.add(optimizedUrl);
      resolve(true);
    };
    img.onerror = () => {
      resolve(false);
    };
    img.src = optimizedUrl;
  });
}

/**
 * Preload an array of image URLs sequentially or in micro-batches
 */
export async function preloadImages(srcList: string[], priority: 'high' | 'auto' | 'low' = 'low') {
  if (typeof window === 'undefined') return;

  const validUrls = srcList.filter(url => url && !preloadedSet.has(getOptimizedImageUrl(url)));
  for (const url of validUrls) {
    await preloadImage(url, priority);
  }
}

/**
 * Safe idle background preload: does not block thread or download heavy assets
 */
export function startIdleBackgroundPreload() {
  if (typeof window === 'undefined') return;

  // Don't run preloader if user is on saveData connection or slow mobile
  if ((navigator as any)?.connection?.saveData) {
    return;
  }

  const runPreload = () => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => preloadImages(CORE_BACKGROUND_IMAGES, 'low'), { timeout: 10000 });
    }
  };

  if (document.readyState === 'complete') {
    setTimeout(runPreload, 6000);
  } else {
    window.addEventListener('load', () => setTimeout(runPreload, 6000), { once: true });
  }
}

